import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import moment from "moment";
import { Loading } from "../../components/ui";
import { AppBar } from "../../components/mobileUi";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  FilterCard,
  SummaryCard,
  ListHeaderRow,
  Fab,
  StatusPill,
  CardKV,
  BORDER,
  MUTED,
  TEXT,
} from "../../components/requestUi";
import {
  DESK,
  DesktopButton,
  DesktopDateInput,
  DesktopPage,
  DesktopPanel,
  DesktopSelect,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { getLeaveRequestsApi, deleteLeaveRequestApi } from "../../api/leave.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH } from "../../config";

const LEAVE_TYPE_NAME = {
  MSHLNP_NGHI_PHEP: "Nghỉ phép",
  MSHLNP_NGHI_HY: "Nghỉ hỷ",
  MSHLNP_NGHI_HIEU: "Nghỉ hiếu",
  MSHLNP_NGHI_KHONG_LUONG: "Nghỉ không lương",
};

const EditIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 20h9" stroke="#2E90FA" strokeWidth="2" strokeLinecap="round" />
    <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" stroke="#2E90FA" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const LeaveList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [fromDate, setFromDate] = useState(moment().subtract(3, "months").startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [sortOrder, setSortOrder] = useState("desc");
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["emp", "leave-list", status, fromDate, toDate, sortOrder],
    queryFn: () =>
      getLeaveRequestsApi({
        page: 1,
        limit: 20,
        sortBy: "createdAt",
        sortOrder,
        requestDateFrom: fromDate,
        requestDateTo: toDate,
        ...(status !== "ALL" ? { status } : {}),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLeaveRequestApi(id),
    onSuccess: () => {
      toast.success("Đã xóa đơn nghỉ phép");
      queryClient.invalidateQueries({ queryKey: ["emp", "leave-list"] });
      setDeleteId(null);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e, "Xóa đơn thất bại"));
      setDeleteId(null);
    },
  });

  const items = data?.items || [];
  const total = data?.pagination?.total ?? data?.total ?? items.length;

  const confirmDialog = (
    <ConfirmDialog
      isOpen={!!deleteId}
      title="Xóa đơn nghỉ phép"
      description="Bạn chắc chắn muốn xóa đơn nghỉ phép này?"
      confirmText="Xóa"
      cancelText="Hủy"
      danger
      loading={deleteMutation.isPending}
      onConfirm={() => deleteMutation.mutate(deleteId)}
      onClose={() => setDeleteId(null)}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  // Cùng query/filter/mutation; chỉ đổi cách hiển thị sang bảng kiểu admin.
  if (isDesktop) {
    const rows = items.map((item) => ({
      id: item._id,
      raw: item,
      createdAt: item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : "-",
      requestDate: item.requestDate
        ? moment(item.requestDate).format("DD/MM/YYYY")
        : item.createdAt
          ? moment(item.createdAt).format("DD/MM/YYYY")
          : "-",
      startDate: item.startDate ? moment(item.startDate).format("DD/MM/YYYY") : "-",
      days: String(item.daysRequested ?? 0),
      typeName:
        item.leaveType?.name || LEAVE_TYPE_NAME[item.leaveTypeCode] || "Nghỉ phép",
      reason: item.reason || "-",
      isPending: !(
        item.status === 2 ||
        item.status === 3 ||
        ["APPROVED", "REJECTED"].includes(String(item.status).toUpperCase())
      ),
    }));

    const openDetail = (row) => navigate(`${EMP_BASE_PATH}/nghi-phep/${row.id}`);

    return (
      <DesktopPage
        title="Đơn nghỉ phép"
        right={
          <DesktopButton onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep/tao`)}>
            + Tạo đơn nghỉ phép
          </DesktopButton>
        }
      >
        <DesktopStatRow items={[{ key: "total", title: "Tổng số đơn", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex align="flex-end" gap="16px" p="16px" flexWrap="wrap">
            <Box w="200px">
              <Text fontSize="13px" color={DESK.label} mb="4px">
                Trạng thái
              </Text>
              <DesktopSelect
                options={[
                  { value: "PENDING", label: "Chờ duyệt" },
                  { value: "APPROVED", label: "Đã duyệt" },
                  { value: "REJECTED", label: "Từ chối" },
                ]}
                value={status === "ALL" ? "" : status}
                placeholder="Tất cả"
                onChange={(v) => setStatus(v || "ALL")}
              />
            </Box>
            <Box w="180px">
              <Text fontSize="13px" color={DESK.label} mb="4px">
                Từ ngày
              </Text>
              <DesktopDateInput value={fromDate} onChange={setFromDate} />
            </Box>
            <Box w="180px">
              <Text fontSize="13px" color={DESK.label} mb="4px">
                Đến ngày
              </Text>
              <DesktopDateInput value={toDate} onChange={setToDate} />
            </Box>
            <Box w="200px">
              <Text fontSize="13px" color={DESK.label} mb="4px">
                Sắp xếp
              </Text>
              <DesktopSelect
                options={[
                  { value: "desc", label: "Mới đến cũ" },
                  { value: "asc", label: "Cũ đến mới" },
                ]}
                value={sortOrder}
                placeholder="Mới đến cũ"
                onChange={(v) => setSortOrder(v || "desc")}
              />
            </Box>
          </Flex>

          <DesktopTable
            loading={isLoading}
            emptyText="Không có đơn nghỉ phép"
            onRowClick={openDetail}
            rows={rows}
            headers={[
              { key: "createdAt", label: "Ngày tạo", width: "10%" },
              { key: "requestDate", label: "Ngày yêu cầu", width: "11%" },
              { key: "startDate", label: "Ngày bắt đầu", width: "11%" },
              { key: "days", label: "Số ngày nghỉ", width: "9%" },
              { key: "typeName", label: "Loại nghỉ phép", width: "13%" },
              { key: "reason", label: "Lý do", align: "left", width: "20%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "12%",
                render: (row) => <StatusPill status={row.raw.status} />,
              },
              {
                key: "actions",
                label: "Thao tác",
                width: "10%",
                render: (row) =>
                  row.isPending ? (
                    <Flex justify="center" gap="8px" onClick={(e) => e.stopPropagation()}>
                      <Flex
                        as="button"
                        w="32px"
                        h="32px"
                        borderRadius="8px"
                        bg="#FFF7E6"
                        align="center"
                        justify="center"
                        onClick={() => openDetail(row)}
                        title="Cập nhật"
                      >
                        <Box as={FiEdit} color="#F79D0D" fontSize="16px" />
                      </Flex>
                      <Flex
                        as="button"
                        w="32px"
                        h="32px"
                        borderRadius="8px"
                        bg="#FFF0F0"
                        align="center"
                        justify="center"
                        onClick={() => setDeleteId(row.id)}
                        title="Xóa"
                      >
                        <Box as={FiTrash2} color="#E5484D" fontSize="16px" />
                      </Flex>
                    </Flex>
                  ) : (
                    "-"
                  ),
              },
            ]}
          />
        </DesktopPanel>

        {confirmDialog}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Danh sách đơn nghỉ phép" onBack={() => navigate(EMP_BASE_PATH)} />
      <Box px="16px" pt="12px">
      <FilterCard
        open={filterOpen}
        onToggle={() => setFilterOpen((v) => !v)}
        status={status}
        onStatusChange={setStatus}
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
      />

      <SummaryCard label="TỔNG SỐ ĐƠN" total={total} />

      <ListHeaderRow
        title="Danh sách đơn nghỉ phép"
        sortLabel={sortOrder === "desc" ? "Mới đến cũ" : "Cũ đến mới"}
        onToggleSort={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
      />

      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
          Không có đơn nghỉ phép
        </Text>
      ) : (
        <Flex direction="column" gap="10px">
          {items.map((item) => {
            const isPending = !(item.status === 2 || item.status === 3 || ["APPROVED", "REJECTED"].includes(String(item.status).toUpperCase()));
            const typeName = item.leaveType?.name || LEAVE_TYPE_NAME[item.leaveTypeCode] || "Nghỉ phép";
            return (
              <Box key={item._id} bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
                <Flex align="center" justify="space-between">
                  <Flex
                    as="button"
                    align="center"
                    gap="8px"
                    flexWrap="wrap"
                    flex="1"
                    onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep/${item._id}`)}
                  >
                    <Text fontSize="14px" color={TEXT}>
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                    </Text>
                    <StatusPill status={item.status} />
                  </Flex>

                  {isPending && (
                    <Menu placement="bottom-end">
                      <MenuButton as="button" onClick={(e) => e.stopPropagation()} aria-label="Thao tác">
                        <Flex w="44px" h="44px" borderRadius="8px" bg="#EFF6FF" align="center" justify="center">
                          <EditIcon />
                        </Flex>
                      </MenuButton>
                      <MenuList minW="180px" borderRadius="12px">
                        <MenuItem
                          icon={<Box as={FiEdit} color="#F79D0D" fontSize="18px" />}
                          onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep/${item._id}`)}
                        >
                          Cập nhật
                        </MenuItem>
                        <MenuItem
                          icon={<Box as={FiTrash2} color="#E5484D" fontSize="18px" />}
                          color="#E5484D"
                          onClick={() => setDeleteId(item._id)}
                        >
                          Xóa
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>

                <Box h="1px" bg={BORDER} my="12px" />
                <Box as="button" w="100%" textAlign="left" onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep/${item._id}`)}>
                  <CardKV label="Ngày yêu cầu" value={item.requestDate ? moment(item.requestDate).format("DD/MM/YYYY") : (item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : "-")} />
                  <Box h="8px" />
                  <CardKV label="Ngày bắt đầu" value={item.startDate ? moment(item.startDate).format("DD/MM/YYYY") : "-"} />
                  <Box h="8px" />
                  <CardKV label="Số ngày nghỉ" value={String(item.daysRequested ?? 0)} />
                  <Box h="8px" />
                  <CardKV label="Loại nghỉ phép" value={typeName} />
                  <Box h="8px" />
                  <CardKV label="Lý do" value={item.reason || "-"} />
                </Box>
              </Box>
            );
          })}
        </Flex>
      )}
      </Box>

      <Fab onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep/tao`)} />

      {confirmDialog}
    </Box>
  );
};

export default LeaveList;
